// BLOCK 2 · Browser-side acquisition identity + draft persistence.
// STATUS: STATIC_PASS. Runtime verification pending staging deploy.
// PRIVACY: behavioral IDs are random and contain no email/name. Contact data only enters
// the persisted draft after the person explicitly fills the identification step.

const SESSION_KEY = "webprofit_acquisition_session_v1";
const VISITOR_KEY = "webprofit_acquisition_visitor_v1";
const DRAFT_KEY = "webprofit_acquisition_draft_v1";

// BLOCK 4 · Analytics contract version.
// STATUS: STATIC_PASS · STAGING_PENDING. Bump this value only when the public
// acquisition/onboarding experience changes materially so cohorts remain comparable.
export const ACQUISITION_EXPERIENCE_VERSION = "wp-acq-2026-09-v3";

function randomId(prefix) {
  // CRA/ESLint in the current WebProfit frontend does not expose `globalThis`
  // in its configured browser globals. Use `window.crypto` when available and
  // keep the existing deterministic fallback for SSR/tests/older browsers.
  const uuid =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : "";
  if (uuid) return `${prefix}_${uuid}`;
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function readStorage(storage, key) {
  try {
    return storage?.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStorage(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {}
}

export function acquisitionVisitorId() {
  if (typeof window === "undefined") return randomId("av");
  const stored = readStorage(window.localStorage, VISITOR_KEY);
  if (stored) return stored;
  const created = randomId("av");
  writeStorage(window.localStorage, VISITOR_KEY, created);
  return created;
}

export function acquisitionSessionId() {
  if (typeof window === "undefined") return randomId("as");
  const stored = readStorage(window.sessionStorage, SESSION_KEY);
  if (stored) return stored;
  const created = randomId("as");
  writeStorage(window.sessionStorage, SESSION_KEY, created);
  return created;
}

export function acquisitionEventId(prefix = "ae") {
  return randomId(prefix).slice(0, 60);
}

export function acquisitionCampaign(searchParams) {
  const params = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(String(searchParams || ""));
  let refererHost = "";
  try {
    refererHost = document.referrer ? new URL(document.referrer).host : "";
  } catch {}
  return {
    source: params.get("utm_source") || "",
    medium: params.get("utm_medium") || "",
    campaign: params.get("utm_campaign") || "",
    content: params.get("utm_content") || "",
    term: params.get("utm_term") || "",
    refererHost,
  };
}

export function acquisitionDeviceMetadata() {
  if (typeof window === "undefined") return { viewportBand: "unknown", deviceType: "unknown" };
  const width = Number(window.innerWidth || 0);
  return {
    viewportBand: width < 600 ? "xs" : width < 900 ? "sm" : width < 1200 ? "md" : "lg+",
    deviceType: width < 700 ? "mobile" : width < 1100 ? "tablet" : "desktop",
  };
}

export function loadAcquisitionDraft() {
  if (typeof window === "undefined") return {};
  try {
    const raw = readStorage(window.localStorage, DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveAcquisitionDraft(patch = {}) {
  if (typeof window === "undefined") return patch;
  const next = { ...loadAcquisitionDraft(), ...patch, updatedAt: new Date().toISOString() };
  writeStorage(window.localStorage, DRAFT_KEY, JSON.stringify(next));
  return next;
}

export function clearAcquisitionDraft() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_KEY);
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

export function formatAcquisitionMetric(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("es-AR", {
    notation: number >= 10000 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(number);
}
